import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Globe, User as UserIcon, LogOut, ShieldCheck, Heart, Menu, X, Sparkles } from 'lucide-react';
import { useI18n, LANGUAGES, LANGUAGE_ORDER } from '../i18n';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useAuth } from '../auth/AuthContext';
import { BrandWordmark } from '../components/BrandLogo';
import DonationModal from '../components/DonationModal';
import AiUsagePill from '../components/AiUsagePill';

/**
 * @param {object} props
 * @param {('assistant'|'vectorize'|'halftone'|'shape'|'neon'|null)} [props.aiFeature]
 *        When set, renders a compact "used/limit · +credits" pill so the
 *        user sees their AI usage at a glance from inside any AI tool.
 *        Pass exactly the feature key used by the worker's quota system.
 */
export default function Header({ aiFeature = null } = {}) {
  const { lang, setLang, t } = useI18n();
  const { user, isAuthed, isPro, isAdmin, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  // Donation modal state lives at the Header level so the button and
  // the dialog stay co-located. The modal portals over everything else.
  const [donateOpen, setDonateOpen] = useState(false);
  // Mobile menu drawer (only used on screens < md). On md+ all the
  // CTAs live in a single horizontal row as before.
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMobile = () => setMobileOpen(false);

  // Primary navigation (Sprint 6) — mock-up inspired. Uses anchor
  // links to Home sections so users on any page can jump straight to
  // the catalog/projects without losing context. /pricing is a real
  // route; /blog and /resources are placeholders for future routes
  // (gracefully render as Links so they're crawlable as soon as the
  // pages ship).
  const PRIMARY_NAV = [
    { label: t('navTools'),     to: '/#templates',  isAnchor: true },
    { label: t('navExamples'),  to: '/examples',    isAnchor: false },
    { label: t('navPricing'),   to: '/pricing',     isAnchor: false },
    { label: t('navBlog'),      to: '/blog',        isAnchor: false },
    { label: t('navResources'), to: '/resources',   isAnchor: false },
  ];

  // Anchor-aware click handler — when on Home and the nav target is a
  // hash link, smooth-scroll instead of navigating.
  const handleNavClick = (link) => (e) => {
    if (link.isAnchor && loc.pathname === '/') {
      const id = link.to.split('#')[1];
      const el = id && document.getElementById(id);
      if (el) {
        e.preventDefault();
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        closeMobile();
      }
    } else {
      closeMobile();
    }
  };

  return (
    <header className="w-full bg-zinc-950 text-zinc-100 sticky top-0 z-50 border-b border-zinc-800">
      <div className="max-w-[1600px] mx-auto px-3 sm:px-5 h-16 flex items-center gap-2 sm:gap-4 md:gap-6">
        {/* Brand: shrinks naturally on mobile (BrandWordmark already
            renders compact); min-w-0 so flex truncation works upstream. */}
        <Link to="/" className="hover:opacity-90 transition-opacity group min-w-0 shrink-0" aria-label="MakerCutt — home">
          <BrandWordmark />
        </Link>

        {/* ── PRIMARY NAV (md+) — Sprint 6 ──
            Mock-up inspired top-bar links. Each link is small uppercase
            chunky text that highlights cyan on hover. Hidden below md
            (the same items live in the mobile drawer below). */}
        <nav className="hidden md:flex items-center gap-1 ml-2">
          {PRIMARY_NAV.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              onClick={handleNavClick(link)}
              className="px-2.5 lg:px-3 py-2 text-[11px] lg:text-xs font-chunky uppercase tracking-wider text-zinc-400 hover:text-cyan-300 hover:bg-zinc-900 rounded-md transition-colors whitespace-nowrap"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* ── DESKTOP / TABLET ROW (md+) ──
            All CTAs in one line. Hidden below md to avoid overflow on
            narrow phones. */}
        <div className="ml-auto hidden md:flex items-center gap-1 min-w-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 font-chunky text-xs uppercase tracking-wider">
                <Globe className="w-3.5 h-3.5 mr-2" />
                <span className="mr-1">{LANGUAGES[lang]?.flag}</span>
                {lang.toUpperCase()}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="font-chunky max-h-[70vh] overflow-y-auto">
              {LANGUAGE_ORDER.map((code) => {
                const meta = LANGUAGES[code];
                if (!meta) return null;
                const active = code === lang;
                return (
                  <DropdownMenuItem
                    key={code}
                    onClick={() => setLang(code)}
                    className={active ? 'bg-cyan-50 text-cyan-700 font-semibold' : ''}
                  >
                    <span className="mr-2 text-base leading-none">{meta.flag}</span>
                    {meta.name}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* AI usage pill — shown only on AI tool pages that opt in
              by passing the `aiFeature` prop. Lets the user see e.g.
              "3/5 · +12 credits" without leaving the page. Click opens
              the credits modal via the same global event used by the
              auto-quota-exceeded hook. */}
          {aiFeature && (
            <AiUsagePill feature={aiFeature} className="hidden sm:inline-flex" />
          )}

          {isAdmin && (
            <Link to="/admin"
                  className="px-3 py-2 text-xs font-chunky uppercase tracking-wider text-fuchsia-300 hover:text-fuchsia-200 hover:bg-zinc-900 rounded-md transition-colors flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5" /> Admin
            </Link>
          )}

          {isAuthed ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 font-chunky text-xs uppercase tracking-wider max-w-[160px]">
                  <UserIcon className="w-3.5 h-3.5 mr-2 shrink-0" />
                  <span className="truncate">{user?.name || user?.email?.split('@')[0]}</span>
                  {isPro && <span className="ml-2 text-[9px] bg-fuchsia-500 text-white px-1.5 rounded shrink-0">PRO</span>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="font-chunky">
                <DropdownMenuItem onClick={() => nav('/account')}>Minha conta</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav('/account/billing')}>Plano & Pagamentos</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav('/account/sessions')}>Sessões</DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); nav('/'); }}>
                  <LogOut className="w-3.5 h-3.5 mr-2" /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login"
                    className="px-3 py-2 text-xs font-chunky uppercase tracking-wider text-zinc-400 hover:text-cyan-400 hover:bg-zinc-900 rounded-md transition-colors">
                Entrar
              </Link>
              <Link to="/signup"
                    className="px-3 py-2 text-xs font-chunky uppercase tracking-wider text-zinc-400 hover:text-cyan-400 hover:bg-zinc-900 rounded-md transition-colors">
                Cadastrar
              </Link>
            </>
          )}
          {/* Donation CTA — visible to everyone (including Pro/Admin),
              because keeping the lights on is a permanent ask. The
              heart icon + warm pink/amber palette differentiates this
              from the cyan "Upgrade" CTA so users don't confuse a tip
              with a subscription. */}
          <button onClick={() => setDonateOpen(true)}
                  className="ml-2 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-chunky uppercase tracking-wider text-rose-300 hover:text-white hover:bg-gradient-to-r hover:from-rose-500 hover:to-amber-500 rounded-md transition-colors">
            <Heart className="w-3.5 h-3.5" fill="currentColor" />
            {t('contribute') || (lang === 'en' ? 'Contribute' : 'Contribuir')}
          </button>
          {!isPro && (
            <Link to="/pricing"
                  className="bg-cyan-600 hover:bg-cyan-500 text-zinc-950 font-chunky uppercase tracking-wider ml-2 rounded-md shadow-[0_0_0_1px_rgba(34,211,238,0.4)] px-3 py-2 text-xs">
              {t('upgrade') || 'Upgrade'}
            </Link>
          )}
        </div>

        {/* ── MOBILE ROW (< md) ──
            Compact: small donate icon + small upgrade pill + hamburger.
            Everything else (idioma, login, conta, admin) is in the
            drawer below to keep the header bar from overflowing. */}
        <div className="ml-auto flex md:hidden items-center gap-1 shrink-0">
          {/* Quick donate icon — keeps the call to action visible
              without taking horizontal space. */}
          <button onClick={() => setDonateOpen(true)}
                  aria-label={t('contribute') || 'Contribuir'}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md text-rose-300 hover:text-white hover:bg-rose-500/20 transition-colors">
            <Heart className="w-4 h-4" fill="currentColor" />
          </button>
          {/* Compact upgrade pill — only icon + 3-letter label so it
              never wraps, only shown to non-Pro users. */}
          {!isPro && (
            <Link to="/pricing"
                  onClick={closeMobile}
                  className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md bg-cyan-600 hover:bg-cyan-500 text-zinc-950 text-[10px] font-chunky uppercase tracking-wider">
              <Sparkles className="w-3 h-3" />
              Pro
            </Link>
          )}
          {/* Hamburger toggles the drawer below. */}
          <button onClick={() => setMobileOpen(o => !o)}
                  aria-label="Menu"
                  aria-expanded={mobileOpen}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* ── MOBILE DRAWER (< md) ──
          Stacks the language picker, auth links and admin link in a
          single column so they never overflow. */}
      {mobileOpen && (
        <div className="md:hidden border-t border-zinc-800 bg-zinc-950">
          <div className="max-w-[1600px] mx-auto px-3 py-3 flex flex-col gap-1">
            {/* Primary nav (Sprint 6) — same set as desktop, stacked
                full-width so they read as a clear menu on phones. */}
            <div className="flex flex-col mb-2 pb-2 border-b border-zinc-800">
              {PRIMARY_NAV.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  onClick={handleNavClick(link)}
                  className="px-3 py-2.5 rounded-md text-sm font-chunky uppercase tracking-wider text-zinc-200 hover:bg-zinc-900 hover:text-cyan-300 transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
            {/* Language picker — opens the same dropdown but rendered
                inline as a labeled trigger that fills the row width. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-chunky uppercase tracking-wider text-zinc-200 hover:bg-zinc-900">
                  <Globe className="w-4 h-4 shrink-0" />
                  <span className="text-base">{LANGUAGES[lang]?.flag}</span>
                  <span className="truncate">{LANGUAGES[lang]?.name || lang.toUpperCase()}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="font-chunky max-h-[60vh] overflow-y-auto w-[calc(100vw-1.5rem)]">
                {LANGUAGE_ORDER.map((code) => {
                  const meta = LANGUAGES[code];
                  if (!meta) return null;
                  const active = code === lang;
                  return (
                    <DropdownMenuItem
                      key={code}
                      onClick={() => { setLang(code); }}
                      className={active ? 'bg-cyan-50 text-cyan-700 font-semibold' : ''}
                    >
                      <span className="mr-2 text-base leading-none">{meta.flag}</span>
                      {meta.name}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {isAdmin && (
              <Link to="/admin"
                    onClick={closeMobile}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-chunky uppercase tracking-wider text-fuchsia-300 hover:bg-zinc-900">
                <ShieldCheck className="w-4 h-4 shrink-0" /> Admin
              </Link>
            )}

            {isAuthed ? (
              <>
                <div className="px-3 pt-2 pb-1 text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 truncate">
                  {user?.name || user?.email?.split('@')[0]}
                  {isPro && <span className="ml-2 text-[9px] bg-fuchsia-500 text-white px-1.5 rounded">PRO</span>}
                </div>
                <Link to="/account" onClick={closeMobile}
                      className="px-3 py-2.5 rounded-md text-sm font-chunky text-zinc-200 hover:bg-zinc-900">Minha conta</Link>
                <Link to="/account/billing" onClick={closeMobile}
                      className="px-3 py-2.5 rounded-md text-sm font-chunky text-zinc-200 hover:bg-zinc-900">Plano & Pagamentos</Link>
                <Link to="/account/sessions" onClick={closeMobile}
                      className="px-3 py-2.5 rounded-md text-sm font-chunky text-zinc-200 hover:bg-zinc-900">Sessões</Link>
                <button onClick={async () => { closeMobile(); await logout(); nav('/'); }}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-chunky text-zinc-200 hover:bg-zinc-900 text-left">
                  <LogOut className="w-4 h-4 shrink-0" /> Sair
                </button>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Link to="/login" onClick={closeMobile}
                      className="px-3 py-2.5 rounded-md text-sm font-chunky uppercase tracking-wider text-zinc-200 hover:bg-zinc-900 text-center border border-zinc-800">
                  Entrar
                </Link>
                <Link to="/signup" onClick={closeMobile}
                      className="px-3 py-2.5 rounded-md text-sm font-chunky uppercase tracking-wider text-cyan-300 hover:text-cyan-200 hover:bg-zinc-900 text-center border border-cyan-800/50">
                  Cadastrar
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      <DonationModal open={donateOpen} onClose={() => setDonateOpen(false)} />
      {/* Thin cyan-to-fuchsia banner — hidden for Pro and Admin users
          (they already have the full feature set, no need to upsell). */}
      {!isPro && !isAdmin && (
        <div className="bg-gradient-to-r from-cyan-600 via-cyan-500 to-fuchsia-500 text-zinc-950 text-xs font-medium">
          <div className="max-w-[1600px] mx-auto px-3 sm:px-5 py-1 flex items-center justify-between gap-2 sm:gap-3 min-w-0">
            <span className="truncate font-medium min-w-0">{t('banner')}</span>
            <Link to="/pricing"
                  className="bg-zinc-950 text-cyan-300 hover:bg-zinc-900 px-2 sm:px-3 py-0.5 rounded-md font-chunky uppercase tracking-wider text-[10px] whitespace-nowrap transition-colors shrink-0">
              {t('upgrade')}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
